import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { lazy, Suspense, useState, type ReactNode } from 'react';
import { IconPhone } from '../components/icons';
import {
  Avatar,
  ErrorNote,
  FlashNote,
  Loading,
  Note,
  PageHead,
  Pill,
  PlaceholderBadge,
  ReasonDialog,
  RideStatusPill,
  SosStatusPill,
  useFlash,
} from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ago,
  dateTime,
  km,
  minutes,
  PAYMENT_STATUS_LABEL,
  percent,
  phone,
  RIDE_STATUS_LABEL,
  rupees,
  shortId,
  time,
  VEHICLE_LABEL,
} from '../lib/format';
import { useFares, useStaffNames } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import { OPEN_STATUSES, type AdminRide, type RideDetail, type RideEvent } from '../lib/types';
import { rideRoute } from '../router';
import { Stars, TAG_LABEL } from './Ratings';

const RouteMap = lazy(() => import('../components/RouteMap'));

function actorName(e: RideEvent, ride: RideDetail, staffName: (id: string | null) => string) {
  switch (e.actorType) {
    case 'RIDER':
      return `rider${ride.rider?.name ? ` ${ride.rider.name.split(' ')[0]}` : ''}`;
    case 'DRIVER':
      return e.actorId === ride.driver?.id && ride.driver?.name ? `driver ${ride.driver.name.split(' ')[0]}` : 'driver';
    case 'ADMIN':
      return `${staffName(e.actorId)} (staff)`;
    default:
      return 'Zoomo Rides';
  }
}

function eventLine(e: RideEvent, ride: RideDetail, staffName: (id: string | null) => string) {
  const d = (e.data ?? {}) as Record<string, unknown>;
  const by = actorName(e, ride, staffName);
  switch (e.type) {
    case 'TRANSITION':
      if (e.toStatus === 'REQUESTED') return { title: 'Booked', meta: `by ${by}`, tone: 'accent' };
      return {
        title: e.toStatus ? RIDE_STATUS_LABEL[e.toStatus] : 'Status changed',
        meta: `by ${by}`,
        tone: e.toStatus === 'COMPLETED' ? 'good' : e.toStatus?.startsWith('CANCELLED') || e.toStatus === 'EXPIRED' ? 'bad' : 'accent',
      };
    case 'OFFERS_SENT': {
      const n = Array.isArray(d.drivers) ? d.drivers.length : 0;
      return { title: `Offered to ${n} driver${n === 1 ? '' : 's'}`, meta: `Round ${String(d.round ?? '')}`, tone: '' };
    }
    case 'OFFER_DECLINED':
      return { title: 'A driver said no', meta: 'Sent on to the next driver', tone: '' };
    case 'PIN_FAILED':
      return { title: 'Wrong PIN entered', meta: `${String(d.failures ?? 1)} wrong so far`, tone: 'bad' };
    case 'CASH_MISMATCH':
      return {
        title: 'Cash different from fare',
        meta: `Collected ${rupees(Number(d.collectedPaise ?? 0))} of ${rupees(Number(d.farePaise ?? ride.farePaise))}`,
        tone: 'bad',
      };
    case 'SOS':
      return { title: 'SOS raised', meta: `by ${by}`, tone: 'bad' };
    default:
      return { title: e.type, meta: '', tone: '' };
  }
}

function Person({
  title,
  name,
  phoneNo,
  rating,
  to,
  children,
}: {
  title: string;
  name: string | null | undefined;
  phoneNo?: string;
  rating?: number | null;
  to?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="card card-pad stack-sm">
      <div className="spread">
        <span className="overline">{title}</span>
        {to}
      </div>
      <div className="person">
        <Avatar name={name} large />
        <div style={{ minWidth: 0 }}>
          <div className="strong truncate" style={{ fontSize: 17 }}>
            {name ?? 'No name yet'}
          </div>
          <div className="muted small">
            {phone(phoneNo)}
            {rating != null ? ` · ★ ${rating.toFixed(1)}` : ''}
          </div>
        </div>
      </div>
      {children}
      {phoneNo && (
        <a className="call-btn" href={`tel:${phoneNo}`}>
          <IconPhone /> Call {title.toLowerCase()}
        </a>
      )}
    </section>
  );
}

const line = (label: ReactNode, value: ReactNode, cls = '') => (
  <>
    <dt className={cls}>{label}</dt>
    <dd className={`num ${cls}`}>{value}</dd>
  </>
);

export function RideDetailPage() {
  const { rideId } = rideRoute.useParams();
  const { isOwner } = useAuth();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const staffName = useStaffNames();
  const fares = useFares();
  const [dialog, setDialog] = useState<'cancel' | 'paid' | null>(null);
  const q = useQuery({ queryKey: qk.ride(rideId), queryFn: () => api.get<RideDetail>(`/admin/rides/${rideId}`) });

  if (q.isLoading) return <Loading rows={6} />;
  if (q.error || !q.data)
    return (
      <div className="stack">
        <PageHead title="Ride" back={{ to: '/rides', label: 'Rides' }} />
        <ErrorNote error={q.error} />
      </div>
    );

  const r = q.data;
  const isOpen = OPEN_STATUSES.includes(r.status);
  const fare = fares.data?.find((f) => f.id === r.fareConfigId);
  const b = r.fareBreakdown;
  const version = fare?.version ?? b?.fareVersion;
  const placeholder = fare?.isPlaceholder ?? b?.isPlaceholderFare;
  const payStatus = r.payment?.status ?? r.paymentStatus ?? null;
  const canMarkPaid = isOwner && r.status === 'COMPLETED' && (payStatus === 'PENDING' || payStatus === 'FAILED');
  const dl = r.driver?.location;

  return (
    <div className="stack">
      <PageHead
        back={{ to: '/rides', label: 'Rides' }}
        title={
          <>
            Ride #{shortId(r.id)} <RideStatusPill status={r.status} />
          </>
        }
        sub={`${VEHICLE_LABEL[r.vehicleType]} · booked ${dateTime(r.requestedAt)}`}
        actions={
          <>
            {canMarkPaid && (
              <button className="btn" onClick={() => setDialog('paid')}>
                Mark as paid
              </button>
            )}
            {isOpen && (
              <button className="btn btn-danger" onClick={() => setDialog('cancel')}>
                Cancel ride
              </button>
            )}
          </>
        }
      />

      <FlashNote flash={flash} />
      {r.sos.length > 0 && (
        <Note tone="bad">
          <strong>SOS on this ride.</strong> {r.sos.map((s) => `${time(s.createdAt)} (${s.status.toLowerCase()})`).join(', ')} ·{' '}
          <Link to="/sos" className="link">
            Open SOS
          </Link>
        </Note>
      )}
      {r.cancelReason && (
        <Note>
          <strong>Cancel reason:</strong> {r.cancelReason}
        </Note>
      )}

      <div className="grid-detail">
        <div className="stack">
          <section className="card card-pad stack-sm" style={{ gap: 18 }}>
            <div className="mini-map tall">
              <Suspense fallback={null}>
                <RouteMap
                  label="Pickup and drop on the map"
                  pickup={r.pickup}
                  drop={r.drop}
                  driver={isOpen && dl ? { lat: dl.lat, lng: dl.lng, vehicleType: r.vehicleType, busy: true } : null}
                />
              </Suspense>
            </div>
            <div className="route">
              <div className="route-point">
                <span className="route-dot" />
                <div style={{ minWidth: 0 }}>
                  <div className="strong">{r.pickup.address ?? 'Pickup'}</div>
                  <div className="small faint num">
                    {r.acceptedAt ? `Driver accepted ${time(r.acceptedAt)}` : 'Pickup'}
                    {r.arrivedAt ? ` · arrived ${time(r.arrivedAt)}` : ''}
                  </div>
                  {r.pickupNote && <div className="small muted">Note for the driver: “{r.pickupNote}”</div>}
                </div>
              </div>
              <div className="route-point">
                <span className="route-dot drop" />
                <div style={{ minWidth: 0 }}>
                  <div className="strong">{r.drop.address ?? 'Drop'}</div>
                  <div className="small faint num">
                    {r.startedAt ? `Started ${time(r.startedAt)}` : 'Drop'}
                    {r.completedAt ? ` · done ${time(r.completedAt)}` : ''}
                  </div>
                </div>
              </div>
            </div>
            <dl className="kv">
              {line('Distance', km(r.distanceMeters))}
              {line('Estimated time', minutes(r.durationSeconds))}
              {isOpen && r.matchDeadline && !r.driver && line('Expires', time(r.matchDeadline))}
              {r.pinFailures > 0 && line('Wrong PINs', r.pinFailures)}
            </dl>
          </section>

          <section className="card card-pad">
            <h2 style={{ marginBottom: 18 }}>What happened</h2>
            <ol className="timeline">
              {r.events.map((e) => {
                const l = eventLine(e, r, staffName);
                return (
                  <li key={e.id} className={l.tone ? `t-${l.tone}` : ''}>
                    <div className="spread" style={{ alignItems: 'baseline' }}>
                      <span className="t-title">{l.title}</span>
                      <span className="t-meta num">{time(e.createdAt)}</span>
                    </div>
                    <div className="t-meta">
                      {l.meta}
                      {e.note ? ` · “${e.note}”` : ''}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          {r.offers.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>
                  Offers <span className="count-chip">{r.offers.length}</span>
                </h2>
              </div>
              <div className="list">
                {r.offers.map((o) => (
                  <Link key={o.id} to="/drivers/$driverId" params={{ driverId: o.driverId }} className="list-item">
                    <div className="grow">
                      <div className="strong">Driver #{shortId(o.driverId)}</div>
                      <div className="small muted">
                        Round {o.round} · {km(o.distanceMeters)} away · {time(o.offeredAt)}
                      </div>
                    </div>
                    <Pill tone={o.status === 'ACCEPTED' ? 'good' : o.status === 'DECLINED' ? 'bad' : 'neutral'}>{o.status.toLowerCase()}</Pill>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="stack">
          <section className="card card-pad stack-sm">
            <div className="spread">
              <span className="overline">Fare</span>
              <span className="row" style={{ gap: 8 }}>
                {version != null && <span className="small muted">Fare version {version}</span>}
                {placeholder && <PlaceholderBadge />}
              </span>
            </div>
            <div className="fare-figure">{rupees(r.farePaise)}</div>
            <div className="small muted">
              Agreed before booking · {r.paymentMethod === 'CASH' ? 'Cash' : r.paymentMethod}
              {payStatus ? ` · ${PAYMENT_STATUS_LABEL[payStatus] ?? payStatus}` : ''}
            </div>
            {b && (
              <>
                <div className="divider" style={{ margin: '4px 0' }} />
                <dl className="kv small">
                  {line('Base', rupees(b.baseFarePaise))}
                  {line(`Distance (${b.distanceKm} km)`, rupees(b.distanceFarePaise))}
                  {line(`Time (${b.durationMin} min)`, rupees(b.timeFarePaise))}
                  {b.minimumTopUpPaise > 0 && line('Minimum fare top-up', rupees(b.minimumTopUpPaise))}
                  {b.bookingFeePaise > 0 && line('Booking fee', rupees(b.bookingFeePaise))}
                  {b.roundingPaise !== 0 && line('Rounding', rupees(b.roundingPaise))}
                </dl>
              </>
            )}
            {(r.discountPaise ?? 0) > 0 || (r.tipPaise ?? 0) > 0 || r.commissionPaise != null || r.payment ? (
              <>
                <div className="divider" style={{ margin: '4px 0' }} />
                <dl className="kv small">
                  {(r.discountPaise ?? 0) > 0 && line('Reward discount', `−${rupees(r.discountPaise)}`)}
                  {(r.tipPaise ?? 0) > 0 && line('Tip', rupees(r.tipPaise))}
                  {r.riderPaysPaise != null && line('Rider pays', rupees(r.riderPaysPaise))}
                  {r.commissionPaise != null && line(`Commission (${percent(r.commissionPercent)})`, rupees(r.commissionPaise))}
                  {r.driverEarningPaise != null && line('Driver earns', rupees(r.driverEarningPaise))}
                  {r.payment && line('Collected', rupees(r.payment.amountCollectedPaise))}
                  {r.payment && r.payment.amountDuePaise !== r.payment.amountCollectedPaise && line('Due', rupees(r.payment.amountDuePaise))}
                </dl>
              </>
            ) : null}
            {r.cancellationFeePaise > 0 && <div className="small muted">Cancellation fee {rupees(r.cancellationFeePaise)}</div>}
          </section>

          <Person
            title="Rider"
            name={r.rider?.name}
            phoneNo={r.rider?.phone}
            rating={r.rider?.rating}
            to={
              r.rider && (
                <Link to="/riders/$riderId" params={{ riderId: r.rider.id }} className="link small">
                  Open
                </Link>
              )
            }
          />

          {r.driver ? (
            <Person
              title="Driver"
              name={r.driver.name}
              phoneNo={r.driver.phone}
              rating={r.driver.rating}
              to={
                <Link to="/drivers/$driverId" params={{ driverId: r.driver.id }} className="link small">
                  Open
                </Link>
              }
            >
              {r.driver.vehicle && (
                <div className="small muted">
                  {r.driver.vehicle.make} {r.driver.vehicle.model} · {r.driver.vehicle.colour} · {r.driver.vehicle.plateDisplay}
                </div>
              )}
              {dl && <div className="small muted">Last position {ago(dl.at)}</div>}
            </Person>
          ) : (
            <section className="card card-pad stack-sm">
              <span className="overline">Driver</span>
              <p className="muted">{isOpen ? 'No driver yet.' : 'No driver took this ride.'}</p>
            </section>
          )}

          {r.ratings.length > 0 && (
            <section className="card card-pad stack-sm">
              <span className="overline">Ratings</span>
              {r.ratings.map((x) => (
                <div key={x.id} className="stack-xs">
                  <div className="row" style={{ gap: 8 }}>
                    <Stars n={x.stars} />
                    <span className="small muted">{x.byType === 'RIDER' ? 'Rider about the driver' : 'Driver about the rider'}</span>
                  </div>
                  {(x.tags.length > 0 || x.comment) && (
                    <div className="small muted">
                      {x.tags.map((t) => TAG_LABEL[t] ?? t).join(', ')}
                      {x.comment ? ` · “${x.comment}”` : ''}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}
          {r.sos.length > 0 && (
            <section className="card card-pad stack-sm">
              <span className="overline">SOS</span>
              {r.sos.map((s) => (
                <div key={s.id} className="spread">
                  <span>
                    {time(s.createdAt)} · {s.note ?? 'No note'}
                  </span>
                  <SosStatusPill status={s.status} />
                </div>
              ))}
            </section>
          )}
        </div>
      </div>

      {dialog === 'cancel' && (
        <ReasonDialog
          title="Cancel this ride?"
          body="The rider and driver are told straight away. This can’t be undone."
          confirmLabel="Cancel ride"
          danger
          placeholder="For example: rider called support to cancel"
          onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            const after = await api.post<AdminRide>(`/admin/rides/${r.id}/cancel`, { reason });
            qc.setQueryData<RideDetail>(qk.ride(r.id), (old) => (old ? { ...old, ...after } : old));
            void qc.invalidateQueries({ queryKey: qk.ride(r.id) });
            void qc.invalidateQueries({ queryKey: qk.liveRides });
            show('Ride cancelled. The rider and driver have been told.');
          }}
        />
      )}
      {dialog === 'paid' && (
        <ReasonDialog
          title="Mark this ride as paid?"
          body={`Records ${rupees(r.payment?.amountDuePaise ?? r.riderPaysPaise ?? r.farePaise)} as paid in cash. Use it when the rider paid some other way, for example at the office.`}
          confirmLabel="Mark as paid"
          placeholder="For example: rider paid at the office"
          onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            await api.post(`/admin/rides/${r.id}/payment/mark-paid`, { method: 'CASH', reason });
            void qc.invalidateQueries({ queryKey: qk.ride(r.id) });
            show('Marked as paid.');
          }}
        />
      )}
    </div>
  );
}
